import _ from 'lodash';
import React, {useContext, useMemo} from 'react';
import {ModifiedTag} from 'src/components/TagAssignment';

import {
  RegistriesSearchContext,
  SingleRegistrySearchContext,
} from '../../Registries/common/RegistriesSearchContextProvider';
import {
  ArtifactCollectionMembershipType,
  CardCollectionType,
  GQLArtifactCollectionMembershipConnectionType,
} from '../../Registries/common/types';
import {usePaginatedArtifactMemberships} from '../../Registries/RegistriesHomepage/hooks';
import {
  ArtifactMembershipsTable,
  MembershipRow,
} from './ArtifactMembershipsTable';

const PAGE_SIZE = 5;

type PanelArtifactHistoryQuickViewProps = {
  onRowClick: (versionIndex: number) => void;
  border?: string;
  artifactCollectionId: string;
  initialMemberships?: GQLArtifactCollectionMembershipConnectionType;
  artifactCollectionName: string;
  projectName: string;
  entityName: string;
  isArtifactMatch: boolean;
  cardCollectionType: CardCollectionType;
  isPortfolio: boolean;
  protectedAliases?: string[];
  showAllVersions?: boolean;
  // this is used to accommodate the all teams model registry page so
  // we can remove the old table panel, and should be removed after
  // the model registry is deprecated
  legacySearchFilter?: string;
};

export const ArtifactsMembershipsTableWrapper = ({
  onRowClick,
  border,
  artifactCollectionId,
  initialMemberships,
  artifactCollectionName,
  projectName,
  entityName,
  isArtifactMatch,
  cardCollectionType,
  isPortfolio,
  protectedAliases,
  showAllVersions,
  legacySearchFilter,
}: PanelArtifactHistoryQuickViewProps) => {
  const searchContext = useMemo(() => {
    return cardCollectionType === CardCollectionType.SINGLE_REGISTRY
      ? SingleRegistrySearchContext
      : RegistriesSearchContext;
  }, [cardCollectionType]);
  const {searchFilter} = useContext(searchContext);

  const {
    data: memberships,
    loading,
    fetchNextPage,
    hasNextPage,
    pageSize,
    setPageSize,
  } = usePaginatedArtifactMemberships({
    artifactCollectionId,
    initialPageSize: PAGE_SIZE,
    // if the user wants to see all versions, we don't want to filter the memberships
    // by the search filter
    searchTerm:
      isArtifactMatch && !showAllVersions
        ? searchFilter || legacySearchFilter
        : undefined,
    // we don't want to pass in initialMemberships when there's a search filter and
    // the user wants to see all versions, because the initialMemberships will be
    // filtered by the search filter
    initialMemberships:
      showAllVersions && searchFilter ? undefined : initialMemberships,
  });
  const chosenMetadataKeys: string[] = [];

  const membershipRows: MembershipRow[] = useMemo(() => {
    return memberships.map((membership: ArtifactCollectionMembershipType) => {
      const rawTags = membership.tags.map((tag: ModifiedTag) => ({
        ...tag,
        attributes: JSON.stringify(tag.attributes),
      }));
      let parsedMetadata: Record<string, any> = {};
      try {
        if (membership.metadata) {
          parsedMetadata = JSON.parse(membership.metadata);
          for (const key of Object.keys(parsedMetadata)) {
            if (
              !chosenMetadataKeys.includes(key) &&
              !key.startsWith('_') &&
              _.isNumber(parsedMetadata[key])
            ) {
              chosenMetadataKeys.push(key);
            }
          }
        }
      } catch (e) {
        console.error({e});
      }
      return {
        ...membership,
        versionIndex: membership.versionIndex ?? 0,
        versionId: membership.artifactId,
        created: membership.createdAt,
        consumingRuns: membership.usedCount,
        size: membership.size.toString(),
        tags: rawTags,
        metadata: parsedMetadata,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberships]);

  return (
    <ArtifactMembershipsTable
      artifactMembershipsData={membershipRows}
      onRowClick={onRowClick}
      chosenMetadataKeys={_.uniq(chosenMetadataKeys)}
      loading={loading && membershipRows.length === 0} // only want to show loader on first page load
      artifactMembershipsActionsData={{
        isPortfolio,
        collectionId: artifactCollectionId,
        collectionName: artifactCollectionName,
        projectName,
        entityName,
        hasMemberships: memberships.length > 0,
      }}
      border={border}
      tableProps={{
        pageSize,
        onChangePageSize: setPageSize,
        hasNextPage,
        fetchingNextPage: loading,
        onFetchNextPage: fetchNextPage,
      }}
      isArtifactMatch={isArtifactMatch}
      cardCollectionType={cardCollectionType}
      protectedAliases={protectedAliases}
    />
  );
};
